// ============================================================
//  PublishedFormContext — shared, localStorage-backed state for
//  every Editor-3 form field on a published event page.
//
//  Single source of truth: localStorage key `em_form_<slug>`
//  (mirrors the `em_cart_<slug>` convention used by useCart).
//  All FormField* blocks (dropped inside a FormContainer) and
//  the standalone Formulaire block read/write through this one
//  provider, mounted once per published page, so values survive
//  refreshes and tab switches until the Recap & Validation form
//  is submitted.
//
//  Field *definitions* (label/type/required) are persisted right
//  alongside each value, not just kept in component state. The
//  page's tabs each remount the Craft.js <Editor>, which unmounts
//  every field block not on the active tab — if we only tracked
//  "currently registered" fields in memory, the Recap & Validation
//  tab would see an empty field list and build an empty form_json.
//  Persisting the full field map means getFormJson()/validate()
//  can always reconstruct the complete set, regardless of which
//  tab happens to be mounted when they're called.
// ============================================================

import React, { createContext, useCallback, useContext, useState } from 'react';

export interface FieldDef {
  nodeId: string;
  label: string;
  type: string;
  required: boolean;
}

interface StoredField extends FieldDef {
  value: string | string[];
}

interface PublishedFormContextValue {
  isPublished: boolean;
  registerField: (def: FieldDef) => void;
  unregisterField: (nodeId: string) => void;
  getFieldValue: (nodeId: string) => string | string[];
  setFieldValue: (nodeId: string, value: string | string[]) => void;
  getError: (nodeId: string) => string;
  fieldCount: number;
  validate: () => boolean;
  getFormJson: () => Record<string, string | string[]>;
  clearStorage: () => void;
}

const defaultCtx: PublishedFormContextValue = {
  isPublished: false,
  registerField: () => {},
  unregisterField: () => {},
  getFieldValue: () => '',
  setFieldValue: () => {},
  getError: () => '',
  fieldCount: 0,
  validate: () => true,
  getFormJson: () => ({}),
  clearStorage: () => {},
};

export const PublishedFormContext =
  createContext<PublishedFormContextValue>(defaultCtx);

export function usePublishedForm(): PublishedFormContextValue {
  return useContext(PublishedFormContext);
}

const toKey = (label: string): string =>
  label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const STORAGE_PREFIX = 'em_form_';

function readStorage(key: string): Record<string, StoredField> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStorage(key: string, data: Record<string, StoredField>): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore quota/serialization errors */
  }
}

interface ProviderProps {
  children: React.ReactNode;
  slug: string;
}

export function PublishedFormProvider({
  children,
  slug,
}: ProviderProps): React.ReactElement {
  const storageKey = STORAGE_PREFIX + (slug || 'default');
  const [fieldsMap, setFieldsMap] = useState<Record<string, StoredField>>(() =>
    readStorage(storageKey)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const registerField = useCallback(
    (def: FieldDef) => {
      setFieldsMap((prev) => {
        const existing = prev[def.nodeId];
        if (
          existing &&
          existing.label === def.label &&
          existing.type === def.type &&
          existing.required === def.required
        ) {
          return prev;
        }
        const next: Record<string, StoredField> = {
          ...prev,
          [def.nodeId]: {
            ...def,
            value: existing?.value ?? (def.type === 'checkbox' ? [] : ''),
          },
        };
        writeStorage(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  // Values are deliberately kept in localStorage even after a field's
  // component unmounts (e.g. the user switched tabs) — they still need
  // to be included in the final form_json submitted from another tab.
  const unregisterField = useCallback((): void => {}, []);

  const setFieldValue = useCallback(
    (nodeId: string, value: string | string[]) => {
      setFieldsMap((prev) => {
        const existing = prev[nodeId];
        const next: Record<string, StoredField> = {
          ...prev,
          [nodeId]: {
            nodeId,
            label: existing?.label ?? '',
            type: existing?.type ?? 'text',
            required: existing?.required ?? false,
            value,
          },
        };
        writeStorage(storageKey, next);
        return next;
      });
      setErrors((prev) => ({ ...prev, [nodeId]: '' }));
    },
    [storageKey]
  );

  const getFieldValue = useCallback(
    (nodeId: string): string | string[] => fieldsMap[nodeId]?.value ?? '',
    [fieldsMap]
  );

  const getError = useCallback(
    (nodeId: string): string => errors[nodeId] ?? '',
    [errors]
  );

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    Object.values(fieldsMap).forEach((f) => {
      if (f.required) {
        const val = f.value;
        const empty =
          !val || (Array.isArray(val) ? val.length === 0 : val.trim() === '');
        if (empty) {
          newErrors[f.nodeId] = `Le champ "${f.label}" est obligatoire.`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [fieldsMap]);

  const getFormJson = useCallback((): Record<string, string | string[]> => {
    const formJson: Record<string, string | string[]> = {};
    Object.values(fieldsMap).forEach((f) => {
      formJson[toKey(f.label)] = f.value ?? '';
    });
    return formJson;
  }, [fieldsMap]);

  const clearStorage = useCallback((): void => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    setFieldsMap({});
    setErrors({});
  }, [storageKey]);

  return (
    <PublishedFormContext.Provider
      value={{
        isPublished: true,
        registerField,
        unregisterField,
        getFieldValue,
        setFieldValue,
        getError,
        fieldCount: Object.keys(fieldsMap).length,
        validate,
        getFormJson,
        clearStorage,
      }}
    >
      {children}
    </PublishedFormContext.Provider>
  );
}
